import { memo } from 'react';
import clsx from '../utils/clsx';
import styles from '../styles/Battlefield.module.css';
import { PING_TYPES } from '../engine/battlefield/pings';

/**
 * The wedges, in the order `wheelChoice` reads them: up, right, down,
 * left. `angle` is degrees clockwise from straight up, which is how the
 * SVG arcs below are laid out too - one source of truth for the geometry.
 */
const WEDGES = [
  { type: 'look', angle: 0, glyph: '◎' },
  { type: 'danger', angle: 90, glyph: '✶' },
  { type: 'going', angle: 180, glyph: '➤' },
  { type: 'question', angle: 270, glyph: '?' },
];

// Sized for the NARROW case: the map often shares its column with the
// combat log and the build bar, and a dial that covers a third of it is
// covering the thing being pointed at.
const OUTER = 52;
const INNER = 19;

/** A quadrant of the ring, as an SVG path. Centred on `angle`. */
function wedgePath(angle) {
  const half = 42; // degrees either side - a gap between quadrants
  const a0 = ((angle - half - 90) * Math.PI) / 180;
  const a1 = ((angle + half - 90) * Math.PI) / 180;
  const pt = (r, a) => `${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`;
  return [
    `M ${pt(INNER, a0)}`,
    `L ${pt(OUTER, a0)}`,
    `A ${OUTER} ${OUTER} 0 0 1 ${pt(OUTER, a1)}`,
    `L ${pt(INNER, a1)}`,
    `A ${INNER} ${INNER} 0 0 0 ${pt(INNER, a0)}`,
    'Z',
  ].join(' ');
}

/** Where a wedge's glyph sits - the middle of its band. */
function glyphAt(angle) {
  const a = ((angle - 90) * Math.PI) / 180;
  const r = (OUTER + INNER) / 2;
  return { x: r * Math.cos(a), y: r * Math.sin(a) };
}

/**
 * PingWheel - what you are about to say, while you are still saying it.
 *
 * A RING, not four labels. Pills hung off a point read as a menu that
 * happened to be near the cursor; a ring reads as one object with the
 * pointer at its centre, which is what the gesture actually is - and it
 * is symmetrical, so no direction looks more default than another.
 *
 * SCREEN SPACE, not the scene's. It is drawn at the pointer and must be
 * the same size whatever the camera is doing - the map's own SVG scales a
 * fixed viewBox, so anything inside it would shrink with the panel.
 *
 * Deaf to pointer events: the gesture is tracked on the map underneath,
 * and a wheel that swallowed the pointer would break the release it
 * exists to interpret.
 */
function PingWheelImpl({ wheel }) {
  if (!wheel) return null;
  const span = OUTER * 2 + 8;

  return (
    <div className={styles.pingWheel} style={{ left: wheel.x, top: wheel.y }}>
      {/* The testid is on the DIAL, not on the wrapper: the wrapper is a
          0×0 anchor point, and a zero-size box counts as hidden to
          anything that asks whether the wheel is visible. */}
      <svg
        data-testid="ping-wheel"
        width={span}
        height={span}
        viewBox={`${-span / 2} ${-span / 2} ${span} ${span}`}
        className={styles.pingDial}
      >
        {/* The hole in the middle IS the cancel: release where you
            pressed and nothing is said. Drawn so that is visible. */}
        <circle className={styles.pingHub} r={INNER - 4} data-active={wheel.choice ? undefined : 'true'} />
        {WEDGES.map((w) => {
          const active = wheel.choice === w.type;
          const meta = PING_TYPES[w.type];
          const at = glyphAt(w.angle);
          return (
            <g key={w.type} data-testid={`ping-wedge-${w.type}`} data-active={active ? 'true' : undefined}>
              <path
                className={clsx(styles.pingSlice, active && styles.pingSliceActive)}
                d={wedgePath(w.angle)}
                fill={meta.colour}
                stroke={meta.colour}
              />
              <text className={styles.pingGlyph} x={at.x} y={at.y} textAnchor="middle" dominantBaseline="central">
                {w.glyph}
              </text>
            </g>
          );
        })}
        {/* Only the chosen one is named. Four labels at once is the wall
            of text the ring was drawn to replace. */}
        {wheel.choice && (
          <text className={styles.pingChoice} y={OUTER + 22} textAnchor="middle" fill={PING_TYPES[wheel.choice].colour}>
            {PING_TYPES[wheel.choice].label}
          </text>
        )}
      </svg>
    </div>
  );
}

export default memo(PingWheelImpl);
